import React from 'react';
import { Box, Typography, Grid, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { formatAddressFromFields, formatCurrency } from './utils';

const PIPrintable = React.forwardRef(({ 
  purchaseOrder, 
  customer, 
  company, 
  lineItems, 
  formatCompanyAddress, 
  getCustomerName,
  showSummary = true,
  showLotBreakdown = true
}, ref) => {
  // Format date without timezone issues
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Convert country code to full name
  const getCountryFullName = (country) => {
    if (!country) return '';
    const countryMap = {
      'US': 'United States',
      'USA': 'United States',
      'United States': 'United States',
      'CA': 'Canada',
      'Canada': 'Canada',
      'MX': 'Mexico',
      'Mexico': 'Mexico',
      'UK': 'United Kingdom',
      'GB': 'United Kingdom',
      'United Kingdom': 'United Kingdom'
    };
    return countryMap[country] || country;
  };

  // Format company address properly
  const getFormattedCompanyAddress = () => {
    if (!company) return '';
    
    let address = '';
    
    if (company.address) address += company.address;
    
    if (company.city || company.state || company.zip_code) {
      address += address ? '\n' : '';
      if (company.city) address += company.city;
      if (company.state) {
        if (company.city) address += ', ';
        address += company.state;
      }
      if (company.zip_code) {
        if (company.city || company.state) address += ' ';
        address += company.zip_code;
      }
    }
    
    if (company.country) {
      address += address ? '\n' : '';
      address += getCountryFullName(company.country);
    }
    
    return address;
  };

  return (
    <div ref={ref} style={{ padding: '24px', backgroundColor: '#fff' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ mb: 1 }}>PROFORMA INVOICE</Typography>
          <Typography variant="body2" color="text.secondary">
            PI #: {purchaseOrder.po_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Date: {formatDate(purchaseOrder.po_date)}
          </Typography>
          {purchaseOrder.requested_delivery_date && (
            <Typography variant="body2" color="text.secondary">
              Requested Delivery: {formatDate(purchaseOrder.requested_delivery_date)}
            </Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6">
            {company?.name || 'Your Company Name'}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {getFormattedCompanyAddress() || 'Address not provided'}
          </Typography>
          {company?.phone && (
            <Typography variant="body2">
              Phone: {company.phone}
            </Typography>
          )}
          {company?.email && (
            <Typography variant="body2">
              Email: {company.email}
            </Typography>
          )}
          {company?.website && (
            <Typography variant="body2">
              Web: {company.website}
            </Typography>
          )}
        </Box>
      </Box>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Bill To:
          </Typography>
          <Typography variant="body2">
            {getCustomerName()}
          </Typography>
          {customer?.company && (
            <Typography variant="body2">
              Company: {customer.company}
            </Typography>
          )}
          {customer?.phone && (
            <Typography variant="body2">
              Phone: {customer.phone}
            </Typography>
          )}
          {customer?.email && (
            <Typography variant="body2">
              Email: {customer.email}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            {/* Space after email */}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0 }}>
            Address:
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0 }}>
            {formatAddressFromFields(
              purchaseOrder.billing_street,
              purchaseOrder.billing_city,
              purchaseOrder.billing_state,
              purchaseOrder.billing_zip,
              purchaseOrder.billing_country
            ) || 'No billing address provided'}
          </Typography>
        </Grid>
        {(purchaseOrder.shipping_street || purchaseOrder.shipping_city || purchaseOrder.shipping_state || purchaseOrder.shipping_zip || purchaseOrder.shipping_country) && (
          <Grid item xs={6}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
              Ship To:
            </Typography>
            <Typography variant="body2">
              {getCustomerName()}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {/* Space after name */}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0 }}>
              Address:
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0 }}>
              {formatAddressFromFields(
                purchaseOrder.shipping_street,
                purchaseOrder.shipping_city,
                purchaseOrder.shipping_state,
                purchaseOrder.shipping_zip,
                purchaseOrder.shipping_country
              )}
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Summary Table - Aggregated by Product */}
      {showSummary && (
        <>
          <Box sx={{ mb: 2, mt: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
              Order Summary
            </Typography>
          </Box>
      <TableContainer sx={{ 
        overflowX: 'auto',
        borderRadius: 3,
        backgroundColor: '#ffffff',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        mb: 4
      }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Size</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>UoM</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const summaryMap = new Map();

              lineItems.forEach(item => {
                if (!item.description) return;

                const key = `${item.product_id || 'no-product'}_${item.variant_id || 'no-variant'}_${item.unit_type || 'Case'}`;

                if (!summaryMap.has(key)) {
                  summaryMap.set(key, {
                    product_name: item.product_name || item.description?.split(' - ')[0] || 'N/A',
                    variant_name: item.variant_name || null,
                    unit_type: item.unit_type || 'Case',
                    units_per_case: item.units_per_case || null,
                    unit_price: parseFloat(item.unit_price) || 0,
                    quantity: 0,
                    total_amount: 0
                  });
                }

                const summary = summaryMap.get(key);
                summary.quantity += parseFloat(item.quantity) || 0;
                summary.total_amount += parseFloat(item.line_total || item.total_price) || 0;
              });

              return Array.from(summaryMap.values()).map((summary, idx) => {
                const isCase = summary.unit_type === 'Case';
                const qtyLabel = isCase
                  ? `${summary.quantity} ${summary.quantity === 1 ? 'Case' : 'Cases'}`
                  : `${summary.quantity}`;

                return (
                  <TableRow key={idx}>
                    <TableCell>
                      <Typography variant="body2">{summary.product_name}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{summary.variant_name || '-'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{summary.units_per_case ? `${summary.units_per_case}U/C` : '-'}</Typography>
                    </TableCell>
                    <TableCell align="center">{qtyLabel}</TableCell>
                    <TableCell align="right">{formatCurrency(summary.unit_price)}</TableCell>
                    <TableCell align="right">{formatCurrency(summary.total_amount)}</TableCell>
                  </TableRow>
                );
              });
            })()}
          </TableBody>
        </Table>
      </TableContainer>
        </>
      )}

      {/* Detailed Lot Breakdown Table */}
      {showLotBreakdown && (
        <>
          <Box sx={{ mb: 2, mt: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
              Lot Breakdown
            </Typography>
          </Box>
      <TableContainer sx={{ 
        overflowX: 'auto',
        borderRadius: 3,
        backgroundColor: '#ffffff',
        border: '1px solid rgba(0, 0, 0, 0.12)'
      }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Size</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>UoM</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Lot Number</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Mfg Date</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Exp Date</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lineItems.map((item, idx) => {
              if (!item.description) return null;
              const isCase = (item.unit_type || 'Case') === 'Case';
              const qty = parseFloat(item.quantity) || 0;
              const qtyLabel = isCase
                ? `${qty} ${qty === 1 ? 'Case' : 'Cases'}`
                : `${qty}`;

              return (
                <TableRow key={idx}>
                  <TableCell>
                    <Typography variant="body2">
                      {item.product_name || item.description?.split(' - ')[0] || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{item.variant_name || '-'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{item.units_per_case ? `${item.units_per_case}U/C` : '-'}</Typography>
                  </TableCell>
                  <TableCell align="center">{qtyLabel}</TableCell>
                  <TableCell align="center">{item.lot_number || '-'}</TableCell>
                  <TableCell align="center">
                    {item.manufactured_date ? formatDate(item.manufactured_date) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {item.expiry_date ? formatDate(item.expiry_date) : '-'}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.line_total || item.total_price)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Box sx={{ width: '300px', mt: 5 }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography>Subtotal:</Typography>
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Typography>{formatCurrency(purchaseOrder.subtotal)}</Typography>
            </Grid>

            {parseFloat(purchaseOrder.discount_percent) > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography>{purchaseOrder.discount_type === 'flat' ? 'Discount:' : `Discount (${purchaseOrder.discount_percent}%):`}</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography>-{formatCurrency(purchaseOrder.discount_amount)}</Typography>
                </Grid>
              </>
            )}

            {parseFloat(purchaseOrder.tax_amount) > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography>Tax ({purchaseOrder.tax_rate}%):</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography>+{formatCurrency(purchaseOrder.tax_amount)}</Typography>
                </Grid>
              </>
            )}

            {parseFloat(purchaseOrder.shipping_fee) > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography>Shipping:</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography>+{formatCurrency(purchaseOrder.shipping_fee)}</Typography>
                </Grid>
              </>
            )}

            {parseFloat(purchaseOrder.handling_fee) > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography>Handling:</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography>+{formatCurrency(purchaseOrder.handling_fee)}</Typography>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={6}>
              <Typography fontWeight="bold">Total:</Typography>
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Typography fontWeight="bold" color="primary">
                {formatCurrency(purchaseOrder.total_before_credit)}
              </Typography>
            </Grid>

            {parseFloat(purchaseOrder.credit_applied) > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography color="success.main">Credit Applied:</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography color="success.main">-{formatCurrency(purchaseOrder.credit_applied)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography fontWeight="bold">Amount Due:</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography fontWeight="bold" color="primary">
                    {formatCurrency(purchaseOrder.amount_due)}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Box>

      {(purchaseOrder.payment_terms || purchaseOrder.payment_method || parseFloat(purchaseOrder.late_fee_rate) > 0 || parseFloat(purchaseOrder.early_payment_discount) > 0) && (
        <Box sx={{ mt: 3 }}>
          <Typography fontWeight="bold" gutterBottom>Payment Terms:</Typography>
          {purchaseOrder.payment_terms && (
            <Typography variant="body2">Payment Due: {purchaseOrder.payment_terms}</Typography>
          )}
          {purchaseOrder.payment_method && (
            <Typography variant="body2">Preferred Method: {purchaseOrder.payment_method}</Typography>
          )}
          {parseFloat(purchaseOrder.early_payment_discount) > 0 && (
            <Typography variant="body2">
              Early Payment: {purchaseOrder.early_payment_discount}% discount if paid within {purchaseOrder.early_payment_days || 10} days
            </Typography>
          )}
          {parseFloat(purchaseOrder.late_fee_rate) > 0 && (
            <Typography variant="body2">
              Late Fee: {purchaseOrder.late_fee_rate}% per month on overdue balances
            </Typography>
          )}
          {purchaseOrder.deposit_required && (
            <Typography variant="body2" color="warning.main">
              Deposit Required: {formatCurrency(purchaseOrder.deposit_amount)} ({purchaseOrder.deposit_percentage}%)
            </Typography>
          )}
        </Box>
      )}

      {purchaseOrder.customer_notes && (
        <Box sx={{ mt: 3 }}>
          <Typography fontWeight="bold" gutterBottom>Notes:</Typography>
          <Typography sx={{ whiteSpace: 'pre-line' }}>{purchaseOrder.customer_notes}</Typography>
        </Box>
      )}

      {purchaseOrder.special_instructions && (
        <Box sx={{ mt: 2 }}>
          <Typography fontWeight="bold" gutterBottom>Special Instructions:</Typography>
          <Typography sx={{ whiteSpace: 'pre-line' }}>{purchaseOrder.special_instructions}</Typography>
        </Box>
      )}

      {purchaseOrder.terms_and_conditions && (
        <Box sx={{ 
          mt: 3, 
          mb: 2,
          p: 2, 
          border: '1px solid #000', 
          pageBreakInside: 'avoid' 
        }}>
          <Typography fontWeight="bold" gutterBottom sx={{ mb: 1 }}>
            Terms & Conditions
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '0.75rem', lineHeight: 1.4 }}>
            {purchaseOrder.terms_and_conditions}
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Thank you for your business!
          </Typography>
        </Box>
        
        {purchaseOrder.prepared_by && purchaseOrder.prepared_by.name && (
          <Box sx={{ textAlign: 'right', minWidth: '250px' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Prepared by:
            </Typography>
            <Box sx={{ 
              fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
              fontSize: '18pt', 
              color: '#000000',
              mb: 1,
              textShadow: '1px 1px 1px rgba(0,0,0,0.1)'
            }}>
              {purchaseOrder.prepared_by.name}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {purchaseOrder.prepared_by.position || 'Sales Representative'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {formatDate(purchaseOrder.prepared_by.date)}
            </Typography>
          </Box>
        )}
      </Box>
    </div>
  );
});

export default PIPrintable;